import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import Typography from '@ui/Typography';
import useStudioAvatarModal from '@avatar/hooks/useStudioAvatarModal';
import Button from '@ui/Button';
import {
  ActorAccessLevel,
  ActorVariant,
  StudioProjectVersion,
} from '../../../../../server/graphql/__generated.types';
import { EventName } from '../../../../context/events/EventsContext';
import { Actor, ActorsListItemProps } from './ActorsListItem';
import AvatarVariantsList from './AvatarVariantsList/AvatarVariantsList';
import StudioClipActionTooltip from '../../../StudioClip/Actions/StudioClipActionTooltip';
import { ActorsListV2PromptProps } from '../modals/AvatarModal/AvatarModalPrompt';

export interface ActorsListProps extends Pick<ActorsListV2PromptProps, 'tags'> {
  actors?: Array<ActorsListItemProps['actor']>;
  activeActor?: Actor;
  recentAvatarVariants?: Array<AvatarWithVariant>;
  activeActorVariant?: ActorVariant;
  /** The maximum number of actors shown, the rest will be accessible via a prompt unless
   * `showSearchOnList` is set to `true`.
   */
  limit?: number;
  setActiveActor: (actor: Actor, actorVariant?: ActorVariant) => void;
  projectVersion: StudioProjectVersion;
}

export type AvatarWithVariant = {
  avatar: Actor;
  variant: ActorVariant;
  variantVersion?: string;
};

export default function ActorsList({
  actors: allActors = [],
  activeActor,
  activeActorVariant,
  limit,
  setActiveActor,
  recentAvatarVariants,
  projectVersion,
  tags,
}: ActorsListProps) {
  const { handleAvatarClick, openAvatarModal } = useStudioAvatarModal({
    setActiveAvatar: setActiveActor,
  });

  const recents = useMemo<Array<AvatarWithVariant>>(() => {
    if (!recentAvatarVariants) return [];
    return limit ? recentAvatarVariants.slice(0, limit) : recentAvatarVariants;
  }, [recentAvatarVariants, limit]);

  const favorites = useMemo<Array<AvatarWithVariant>>(() => {
    const favoritesList: Array<AvatarWithVariant> = [];

    allActors
      .filter(a => a.accessLevel !== ActorAccessLevel.Locked)
      .forEach(avatar => {
        avatar.variants.forEach(variant => {
          if (variant.userSettings?.favoritedAt) {
            favoritesList.push({
              avatar,
              variant,
            });
          }
        });
      });

    return favoritesList.sort((a, b) => {
      return a.avatar.name.localeCompare(b.avatar.name);
    });
  }, [allActors]);

  return (
    <>
      <Box marginBottom={3}>
        <Typography variant="h5" bold>
          Voices
        </Typography>
      </Box>
      <AvatarVariantsList
        activeAvatarId={activeActor?.id}
        activeAvatarVariantId={activeActorVariant?.id}
        listType={'recents'}
        avatarVariants={recents}
        handleAvatarClick={handleAvatarClick}
        projectVersion={projectVersion}
      />
      {
        <StudioClipActionTooltip
          title="Open voice selection modal"
          placement="bottom"
          enterDelay={750}
        >
          <Button
            variant="secondary"
            aria-label="Click to see all voices."
            onClick={() =>
              openAvatarModal({
                event: { type: EventName.ActorSelectionPromptOpened },
                payload: {
                  activeAvatarId: activeActor?.id,
                  activeVariantId: activeActorVariant?.id,
                  studioVersion: projectVersion,
                  tags,
                },
              })
            }
            size="small"
            fullWidth
          >
            {'See all voices'}
          </Button>
        </StudioClipActionTooltip>
      }

      <Box marginTop={4} paddingBottom={6}>
        <AvatarVariantsList
          activeAvatarId={activeActor?.id}
          activeAvatarVariantId={activeActorVariant?.id}
          listType={'favorites'}
          avatarVariants={favorites}
          handleAvatarClick={handleAvatarClick}
          projectVersion={projectVersion}
        />
      </Box>
    </>
  );
}
